import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphConfig } from './graphConfig';
import { sevenConfig } from './sevenConfig';
import { Card, CardContent, Typography, Button, Grid, Chip, Stack, TextField, Backdrop, CircularProgress, Modal, Box, Tooltip } from '@mui/material';

const MessageSender = ({ selectedGroupIds = [] }) => {
    const { instance, accounts } = useMsal();
    const [message, setMessage] = useState('');
    const [recipients, setRecipients] = useState([]);
    const [customNumbers, setCustomNumbers] = useState([]);
    const [currentNumber, setCurrentNumber] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [responseModalOpen, setResponseModalOpen] = useState(false);
    const [responseData, setResponseData] = useState(null);
    const [responseError, setResponseError] = useState(null);

    useEffect(() => {
        const fetchGroupMembers = async () => {
            setLoading(true);
            const allMembers = [];
            try {
                const request = {
                    scopes: ["User.Read"],
                    account: accounts[0],
                };
    
                const response = await instance.acquireTokenSilent(request);
    
                for (const groupId of selectedGroupIds) {
                    let nextLink = `${graphConfig.groupList.endpoint}/${groupId}/members`;
                
                    // Fetch all pages of members using pagination
                    while (nextLink) {
                        const membersResponse = await fetch(nextLink, {
                            headers: {
                                Authorization: `Bearer ${response.accessToken}`,
                            },
                        });
                        const membersData = await membersResponse.json();
                        allMembers.push(...membersData.value);
                        
                        // Check if there's a next page
                        nextLink = membersData['@odata.nextLink'] || null;
                    }
                }
    
                const uniqueMembers = allMembers.reduce((acc, current) => {
                    const x = acc.find((item) => item.id === current.id);
                    return x ? acc : acc.concat([current]);
                }, []);
    
                setRecipients(
                    uniqueMembers.filter(
                        (member) => member.givenName && member.surname
                    )
                );
            } catch (error) {
                console.error("Fehler beim Abrufen der Mitglieder:", error);
            } finally {
                setLoading(false);
            }
        };
    
        if (Array.isArray(selectedGroupIds) && selectedGroupIds.length > 0) {
            fetchGroupMembers();
        } else if (Array.isArray(selectedGroupIds) && selectedGroupIds.length === 0) {
            setRecipients([]);
        }
    }, [selectedGroupIds, instance, accounts]);

    const handleSendMessage = async () => {
        if (recipients.length === 0 && customNumbers.length === 0) {
            alert('Bitte wählen Sie mindestens einen Empfänger aus.');
            return;
        }
        if (!message) {
            alert('Bitte geben Sie eine Nachricht ein.');
            return;
        }

        setConfirmDialogOpen(true);
    };

    const handleConfirmSend = async () => {
        try {
            const request = {
                scopes: ["User.Read"],
                account: accounts[0],
            };

            const tokenResponse = await instance.acquireTokenSilent(request);
            const userEmail = accounts[0].username;

            setLoading(true);
            const allRecipients = [...recipients.map(r => r.mobilePhone), ...customNumbers].filter(Boolean);
            
            // Prepare form data for seven.io API
            const formData = new URLSearchParams();
            formData.append('to', allRecipients.join(','));
            formData.append('text', message);
            formData.append('from', sevenConfig.sender);

            const sendResponse = await fetch(sevenConfig.endpoint, {
                method: 'POST',
                headers: {
                    'X-Api-Key': sevenConfig.apiKey,
                    'Accept': 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: formData.toString(),
            });

            const responseData = await sendResponse.json();

            if (!sendResponse.ok) {
                throw new Error(responseData.error_text || 'Fehler beim Senden der Nachricht');
            }

            // Transform seven.io response to match expected format
            const successCount = responseData.messages?.filter(msg => msg.success).length || 0;
            const totalCount = responseData.messages?.length || 0;
            
            const transformedResponse = {
                send_success: successCount,
                send_required: totalCount,
                total_price: responseData.total_price,
                balance: responseData.balance,
                messages: responseData.messages
            };

            setResponseData(transformedResponse);
            setMessage('');
            setResponseModalOpen(true);
        } catch (error) {
            console.error('Fehler:', error);
            setResponseError(error.message || 'Fehler beim Senden der Nachricht');
            setResponseModalOpen(true);
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
        }
    };

    const handleDelete = (memberId) => {
        setRecipients((prevRecipients) => prevRecipients.filter(member => member.id !== memberId));
    };

    const formatNumber = (number) => {
        const formattedNumber = number.replace(/[^0-9+]/g, '');
        return formattedNumber.startsWith('0') ? formattedNumber : `+49${formattedNumber}`;
    };

    const handleAddNumber = () => {
        const formattedNumber = formatNumber(currentNumber);
        if (!formattedNumber) {
            alert('Ungültige deutsche Handynummer');
            return;
        }
        if (!customNumbers.includes(formattedNumber)) {
            setCustomNumbers([...customNumbers, formattedNumber]);
        } else {
            const index = customNumbers.indexOf(formattedNumber);
            const chipElement = document.getElementById(`custom-number-${index}`);
            if (chipElement) {
                chipElement.classList.add('vibrate');
                setTimeout(() => chipElement.classList.remove('vibrate'), 1000);
            }
        }
        setCurrentNumber('');
        setModalOpen(false);
    };

    const handleDeleteNumber = (numberToDelete) => {
        setCustomNumbers(customNumbers.filter(number => number !== numberToDelete));
    };

    const validRecipients = recipients.filter(member => member.mobilePhone);
    const invalidRecipients = recipients.filter(member => !member.mobilePhone);

    const handleCloseResponseModal = () => {
        setResponseModalOpen(false);
        setResponseData(null);
        setResponseError(null);
    };

    return (
        <Card style={{ margin: '20px', padding: '20px', width: '100%' }}>
            <style>
                {`
                .vibrate {
                    animation: vibrate 0.5s;
                }

                @keyframes vibrate {
                    0% { transform: translate(0); }
                    25% { transform: translate(-2px, 2px); }
                    50% { transform: translate(2px, -2px); }
                    75% { transform: translate(-2px, 2px); }
                    100% { transform: translate(0); }
                }
                `}
            </style>
            <CardContent>
                <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: '20px' }}>
                    <Typography variant="h6">
                        Empfänger
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {validRecipients.length + invalidRecipients.length + customNumbers.length} Empfänger
                    </Typography>
                </Grid>
                <Grid container spacing={1} style={{ marginBottom: '20px' }}>
                    {recipients && recipients.length > 0 && recipients[0] && recipients.map((recipient, index) => (
                        <Grid item key={index}>
                            <Tooltip title={recipient.mobilePhone || 'Keine Nummer verfügbar'}>
                                <Chip label={`${recipient.givenName} ${recipient.surname}`} onDelete={() => handleDelete(recipient.id)} color={recipient.mobilePhone ? 'primary' : 'secondary'} />
                            </Tooltip>
                        </Grid>
                    ))}
                    {customNumbers.map((number, index) => (
                        <Grid item key={index} id={`custom-number-${index}`}>
                            <Chip label={number} onDelete={() => handleDeleteNumber(number)} />
                        </Grid>
                    ))}
                    <Grid item>
                        <Chip label="+" onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }} />
                    </Grid>
                </Grid>
                <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                    <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, backgroundColor: 'white', padding: '20px', borderRadius: '8px' }}>
                        <Button onClick={() => setModalOpen(false)} style={{ position: 'absolute', top: '13px', right: '16px', minWidth: 'auto', padding: '5px', background: 'none', border: 'none', boxShadow: 'none', color: 'black' }}>
                            X
                        </Button>
                        <Typography variant="h6" gutterBottom style={{ marginBottom: '20px' }}>
                            Neue Handynummer hinzufügen
                        </Typography>
                        <TextField
                            autoFocus
                            label="Handynummer"
                            variant="outlined"
                            fullWidth
                            value={currentNumber}
                            onChange={(e) => setCurrentNumber(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && /^(01|\+49|0049)\d{7,25}$/.test(e.target.value)) {
                                    handleAddNumber();
                                }
                            }}
                            style={{ marginBottom: '20px' }}
                        />
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="error">
                                {!(/^(01|\+49|0049)\d{7,25}$/.test(currentNumber)) && currentNumber ? 'Ungültige deutsche Handynummer' : ' '}
                            </Typography>
                            <Button variant="contained" onClick={handleAddNumber} disabled={!(/^(01|\+49|0049)\d{7,25}$/.test(currentNumber))} style={{ marginLeft: 'auto' }}>
                                Hinzufügen
                            </Button>
                        </Grid>
                    </Box>
                </Modal>
                <TextField
                    label="Nachricht"
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 160))}
                    placeholder="Nachricht eingeben..."
                    style={{ marginBottom: '5px', marginTop: 0 }}
                />
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '20px' }}>
                    {message.length} / 160 Zeichen
                </Typography>
                <Grid container justifyContent="flex-end" alignItems="center">
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSendMessage} 
                        disabled={!message || (validRecipients.length + customNumbers.length) === 0}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                color: 'white',
                            },
                            '&:active': {
                                color: 'white',
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(255, 255, 255, 0.5)'
                            }
                        }}
                    >
                        Nachricht senden
                    </Button>
                </Grid>
            </CardContent>
            <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Modal
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                aria-labelledby="confirm-modal-title"
                aria-describedby="confirm-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <Typography id="confirm-modal-title" variant="h6" component="h2">
                        Nachricht bestätigen
                    </Typography>
                    <Typography id="confirm-modal-description" sx={{ mt: 2 }}>
                        Möchten Sie diese Nachricht <br />an {[...recipients.map(r => r.mobilePhone), ...customNumbers].filter(Boolean).length} Empfänger wirklich senden?
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setConfirmDialogOpen(false)} color="inherit" sx={{
                                
                                '&:hover': {
                                    color: 'white',
                                },
                                '&:active': {
                                    color: 'white',
                                }
                            }}>
                            Abbrechen
                        </Button>
                        <Button 
                            onClick={handleConfirmSend} 
                            variant="contained" 
                            color="primary"
                            sx={{
                                color: 'white',
                                '&:hover': {
                                    color: 'white',
                                },
                                '&:active': {
                                    color: 'white',
                                },
                                '&.Mui-disabled': {
                                    color: 'rgba(255, 255, 255, 0.5)'
                                }
                            }}
                        >
                            SENDEN
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal
                open={responseModalOpen}
                onClose={handleCloseResponseModal}
                aria-labelledby="response-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    alignContent: 'center',
                    textAlign: 'center',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    {responseData ? (
                        <>
                            <Typography id="response-modal-title" variant="h6" component="h2" 
                                      sx={{  mb: 2 }}>
                                ✓ Nachricht gesendet
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Erfolgreich:</strong> {responseData.send_success} von {responseData.send_required} Nachrichten
                            </Typography>
                            {responseData.total_price !== undefined && (
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Kosten:</strong> {responseData.total_price.toFixed(2)} €
                                </Typography>
                            )}
                            {responseData.balance !== undefined && (
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    <strong>Guthaben:</strong> {responseData.balance.toFixed(2)} €
                                </Typography>
                            )}
                            {responseData.send_required !== responseData.send_success && (
                                <Typography variant="body1" color="warning.main" sx={{ mt: 2 }}>
                                    ⚠ {responseData.send_required - responseData.send_success} Nachrichten konnten nicht gesendet werden
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography id="response-modal-title" variant="h6" component="h2" 
                                      sx={{ color: 'error.main', mb: 2 }}>
                                Fehler beim Senden
                            </Typography>
                            <Typography variant="body1">
                                {responseError}
                            </Typography>
                        </>
                    )}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button 
                            onClick={handleCloseResponseModal} 
                            variant="contained" 
                            color="primary"
                            sx={{
                                color: 'white',
                                '&:hover': {
                                    color: 'white',
                                },
                                '&:active': {
                                    color: 'white',
                                }
                            }}
                        >
                            Schließen
                        </Button>
                        
                    </Box>
                </Box>
            </Modal>
        </Card>
    );
};

export default MessageSender;
